/* GASSISA
=========================================================================================
* Descripcion:
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 17/07/2026 | Lic. Jose Luis Aragon Cervantes  | Dashboard administracion tenant
* ---------------------------------------------------------------------------------------
*/

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import { useSesion } from "../../context/System/SesionContext";
import { routeLogin } from "../../settings/routeConfig";
import RequestAdditionalAccessDialog from "../tenant/RequestAdditionalAccessDialog";

export default function FrmTenantAdminDashboard() {
  const navigate = useNavigate();
  const { tenantAdminDevice, tenantAdminStatus, refreshTenantProfiles } = useSesion();
  const [openIntegrate, setOpenIntegrate] = useState(false);

  const tenantKey = tenantAdminStatus?.tenantKey ?? tenantAdminDevice?.tenantKey ?? "";
  const tenantName = tenantAdminStatus?.tenantName ?? "";
  const tenantStatus = tenantAdminStatus?.tenantStatus ?? "";
  const databaseName = tenantAdminStatus?.databaseName ?? tenantAdminStatus?.provisionedDatabaseName ?? "";
  const devicePublicId = tenantAdminStatus?.devicePublicId ?? tenantAdminDevice?.devicePublicId ?? "";
  const adminEmail = tenantAdminStatus?.adminEmail ?? tenantAdminDevice?.adminEmail ?? "";

  const statusColor = tenantStatus === "ACTIVE" ? "success" : tenantStatus === "SUSPENDED" ? "error" : "default";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography component="h1" variant="h5" fontWeight={900}>
          Administracion ARSync
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Panel de administracion del tenant. Estado de cuenta y administracion de dispositivos.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <AdminPanelSettingsOutlinedIcon color="warning" />
            <Typography variant="h6" fontWeight={800}>Datos del cliente</Typography>
          </Stack>
          <Divider />
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ApartmentOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Empresa</Typography>
              <Typography variant="body1" fontWeight={600}>{tenantName || "---"}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <KeyOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Licencia</Typography>
              <Typography variant="body1" fontWeight={600}>{tenantKey || "---"}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <BadgeOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Correo administrador</Typography>
              <Typography variant="body1" fontWeight={600}>{adminEmail || "---"}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {tenantStatus === "ACTIVE" ? <CheckCircleOutlineIcon fontSize="small" color="success" /> : <ErrorOutlineIcon fontSize="small" color="error" />}
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Estado</Typography>
              <Chip size="small" label={tenantStatus || "---"} color={statusColor} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <StorageOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Base de datos</Typography>
              <Typography variant="body1" fontWeight={600}>{databaseName || "---"}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <DevicesOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>Equipo autorizado</Typography>
              <Typography variant="body1" fontWeight={600}>{devicePublicId || "---"}</Typography>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Button variant="contained" startIcon={<DevicesOutlinedIcon />} onClick={() => navigate("/administraciondispositivos")}>
          Administracion de dispositivos moviles
        </Button>
        <Button variant="outlined" startIcon={<DevicesOutlinedIcon />} onClick={() => setOpenIntegrate(true)}>
          Integrar este equipo a otra licencia
        </Button>
        <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate(routeLogin)}>
          Regresar a login
        </Button>
      </Stack>
      <RequestAdditionalAccessDialog
        open={openIntegrate}
        onClose={() => setOpenIntegrate(false)}
        onDone={() => refreshTenantProfiles?.()}
      />
    </Box>
  );
}
